/**
 * @module TokenBridge
 * @package @geenius/adapters/react-native
 * @description Bridge exposing semantic color tokens for native surfaces.
 */

import { blue } from "@geenius/tokens";

const palette = blue.light;

/**
 * OKLCH semantic colors shared by React Native adapter components.
 *
 * @remarks Values are native style color strings grouped by brand, status, and surface roles.
 * @example
 * ```tsx
 * <View style={{ backgroundColor: semanticColors.surface.card }} />
 * ```
 */
export const semanticColors = {
  brand: {
    bg: palette.primary,
    bgSoft: palette["primary-subtle"],
    border: palette["border-hover"],
    onBrand: palette["primary-fg"],
    text: palette.primary,
  },
  status: {
    connected: {
      bg: palette["success-subtle"],
      border: palette.success,
      text: palette.success,
    },
    disconnected: {
      bg: palette.bg,
      border: palette["border-strong"],
      text: palette["text-secondary"],
    },
    error: {
      bg: palette["danger-subtle"],
      border: palette.danger,
      text: palette.danger,
      textStrong: palette.danger,
    },
    initializing: {
      bg: palette["info-subtle"],
      border: palette.info,
      text: palette.info,
    },
    warning: {
      bg: palette["warning-subtle"],
      border: palette.warning,
      text: palette.warning,
    },
  },
  surface: {
    bg: palette.bg,
    border: palette.border,
    borderStrong: palette["border-strong"],
    card: palette.card,
    muted: palette["bg-muted"],
    mutedStrong: palette["bg-subtle"],
    overlay: palette["surface-overlay"],
    skeleton: palette["bg-subtle"],
    skeletonStrong: palette["border-strong"],
    text: palette.text,
    textMuted: palette["text-tertiary"],
    textSoft: palette["text-secondary"],
  },
} as const;

/**
 * Type of the native semantic color token object.
 *
 * @example
 * ```ts
 * const palette: SemanticColors = semanticColors;
 * ```
 */
export type SemanticColors = typeof semanticColors;
